/**
 * Formats a number with K/M/B suffixes for large values.
 *
 * @param value - The number to format
 * @param options - Formatting options
 * @param options.decimals - Number of decimal places to show (default: 1)
 * @param options.threshold - Minimum value to apply formatting (default: 1000)
 * @returns Formatted string (e.g., "21.4K", "1.2M", "999")
 *
 * @example
 * formatNumber(999) // "999"
 * formatNumber(1000) // "1K"
 * formatNumber(21400) // "21.4K"
 * formatNumber(1200000) // "1.2M"
 * formatNumber(1500000000) // "1.5B"
 */
export function formatNumber(
  value: number,
  options: { decimals?: number; threshold?: number } = {},
): string {
  const { decimals = 1, threshold = 1000 } = options;

  if (value < threshold) {
    return value.toString();
  }

  const units = [
    { suffix: "B", value: 1e9 },
    { suffix: "M", value: 1e6 },
    { suffix: "K", value: 1e3 },
  ];

  for (const { value: unitValue, suffix } of units) {
    if (value >= unitValue) {
      const formatted = value / unitValue;
      // Remove trailing zeros and decimal point if not needed
      const rounded = formatted.toFixed(decimals);
      const cleaned = parseFloat(rounded).toString();
      return `${cleaned}${suffix}`;
    }
  }

  return value.toString();
}
